import React, { useState } from 'react';
import { Drawer, RadioButton } from '../../../../components';
import { useMutate, useAppAndBranchId } from '../../../../hooks';
import { api } from '../../api';
import { useToast, uuid } from '../../../../../../../../helpers/commonFunction';
import style from './add-card-choice.module.scss';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { TChoice, Choice } from './Choice';
import { sortableOptions } from '../../../../../soloJourney/properties/message/components';
import { ReactSortable } from 'react-sortablejs';
import { Toggles } from './Toggles';
import { ContentMode } from '../../../../types';
import { validateList } from '../../../../utils/validation';
import { errorMessage } from '../../../../utils/error';

export type CardChoice = {
	title: string;
	orientation: 'V' | 'H';
	choices: { id: string; label: string; description: string; _id: string }[];
	// autocomplete specific fields
	isMulti?: boolean;
	isClearable?: boolean;
	isCreatable?: boolean;
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: CardChoice & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isAutocomplete?: boolean;
	isPreview?: boolean;
};

// AddCardChoice will be treated as 'AddAutocomplete'
// based on the 'isAutocomplete' boolean flag.
// This is done since both these components are very similar with
// minor changes in schema and UI
export function AddCardChoice(props: Props) {
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		isOpen,
		close,
		initialValue,
		refetchContent,
		onCreate,
		isAutocomplete = false,
		isPreview = false,
	} = props;

	// the _id generated column is used to preserve keys between rerenders
	const [state, setState] = useState<CardChoice>(() => {
		if (initialValue) {
			return {
				...initialValue,
				choices: initialValue.choices.map((el) => ({ ...el, _id: uuid() })),
			};
		}
		return {
			choices: [],
			title: '',
			orientation: 'V',
		};
	});

	const [mode, setMode] = useState(props.mode ?? 'create');

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);

	const [error, setError] = useState<{
		title: string;
		choices: Record<string, string>[];
		minChoice: string;
	}>({
		title: '',
		choices: [],
		minChoice: '',
	});

	function clearErrorChoice(index: number, key: string) {
		setError((prev) => ({
			...prev,
			choices: prev.choices.map((ch, i) =>
				i === index ? { ...ch, [key]: '' } : ch,
			),
		}));
	}

	function clearError(key: keyof typeof error) {
		setError((prev) => ({ ...prev, [key]: '' }));
	}

	function addChoice() {
		setState((prev) => ({
			...prev,
			choices: [
				...prev.choices,
				{
					id: '',
					label: '',
					description: '',
					_id: uuid(),
				},
			],
		}));
	}

	function validate() {
		const choicesValidation = state.choices
			.map((choice, i) => {
				const rulesFn = (prefix: string) => {
					return [
						{
							message: `${prefix} can't be empty`,
							fn: (value: string) => value.trim().length <= 0,
						},
					];
				};
				const validateRules = [
				 { name: 'id', value: choice.id, rules: [
					 ...rulesFn('Id'),
					 {
						 message: 'Id already exists',
						 fn: (value: string) => {
							 const previousChoices = state.choices.slice(0, i).map(el => el.id.trim())
							 return previousChoices.includes(value.trim())
						 }
					 }
				 ] },
					{ name: 'label', value: choice.label, rules: rulesFn('Label') },
				]
				if(!isAutocomplete) {
					validateRules.push({
						name: 'description',
						value: choice.description,
						rules: [
						  { 
								message: `Description can't be empty`, 
								fn: (value: string) => value.trim().length <= 0,
							},
							{
								message: `Description exceeded maximum of 100 characters`, 
								fn: (value: string) => value.trim().length > 100,
							}
						]
					})
				}
				return validateList(validateRules)
			})
			.map((item, i) => ({ ...item, _id: state.choices[i]._id }));

		const error = {
			choices: choicesValidation,
			title: !state.title.trim().length ? "Title can't be empty" : '',
			minChoice: !state.choices.length
				? `Atleast one ${isAutocomplete ? 'option' : 'choice'} should be included`
				: '',
		};

		if (error.minChoice) {
			WarningToast(error.minChoice);
		}

		let hasError = false;

		if (error.title || error.minChoice) {
			hasError = true;
		}

		let choicesError = false
		error.choices.forEach(({ _id, ...rest }) => {
		  if(Object.keys(rest).length !== 0) {
			  hasError = true
				choicesError = true
			}
		});

		if(choicesError) {
		  WarningToast(`${isAutocomplete ? 'Option' : 'Choice'}s contains empty or incorrect values`)
		}

		setError(error);
		return !hasError;
	}

	function updateChoice(key: keyof TChoice, value: any, index: number) {
		setState((prev) => ({
			...prev,
			choices: prev.choices.map((el, i) =>
				i === index ? { ...el, [key]: value } : el,
			),
		}));
	}

	function removeChoice(index: number) {
		setState((prev) => ({
			...prev,
			choices: prev.choices.filter((_, i) => i !== index),
		}));
	}

	return (
		<Drawer
			mode={mode}
			isPreview={isPreview}
			loading={createLoading || updateLoading}
			visible={isOpen}
			contentType={isAutocomplete ? 'autocomplete' : 'cardChoice'}
			onClose={() => {
				setState({
					title: '',
					choices: [],
					orientation: 'V',
				});
				close();
			}}
			onSave={async () => {
				if (mode !== 'view' && !validate()) {
					return;
				}

				const payload: Record<string, any> = {
					branchId,
					contentType: isAutocomplete ? 'autoComplete' : 'cardChoice',
					contentValue: {
						title: state.title.trim(),
					},
				};

				if (isAutocomplete) {
					payload.contentValue.isMulti = Boolean(state.isMulti);
					payload.contentValue.isCreatable = Boolean(state.isCreatable);
					payload.contentValue.isClearable = Boolean(state.isClearable);
					payload.contentValue.options = state.choices.map((el) => {
						return { id: el.id, label: el.label };
					});
				} else {
					payload.contentValue.style = {
						orientation: state.orientation,
					};
					payload.contentValue.choices = state.choices.map(
						({ _id, ...rest }) => rest,
					);
				}

				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
					const entity = isAutocomplete ? 'autocomplete' : 'card choice';
					ErrorToast(errorMessage(mode, entity, err.code));
				}
			}}
		>
			<div className={style.addCardChoice}>
				<div className={style.titleWrapper}>
					<label>Title</label>
					<input
						autoFocus
						disabled={mode === 'view'}
						placeholder="Enter title"
						value={state.title}
						onChange={(e) => {
							clearError("title")
							const value = e.currentTarget.value;
							setState((prev) => ({ ...prev, title: value }));
						}}
					/>
					<span className={style.error}>{error.title}</span>
				</div>

				{isAutocomplete && (
					<Toggles state={state} setState={setState} mode={mode} />
				)}

				{!isAutocomplete && (
					<div className={style.choiceStyle}>
						<p style={{ color: '#767B83', marginBottom: 4 }}>
							Choice Orientation
						</p>
						<RadioButton
							disabled={mode === 'view'}
							options={options}
							selected={state.orientation}
							onSelect={(value: any) =>
								setState((prev) => ({ ...prev, orientation: value }))
							}
						/>
					</div>
				)}

				<div className={style.addChoices}>
					{(mode !== 'view' || state.choices.length > 0) && (
						<p>{isAutocomplete ? 'Options' : 'Choices'}</p>
					)}
					{mode !== 'view' && (
						<>
							<div className={style.line} />
							<button className={style.add} onClick={addChoice}>
								<img src={AddIcon} alt="add" />
								<span>{isAutocomplete ? 'Add Options' : 'Add Choices'}</span>
							</button>
						</>
					)}
				</div>

				<p className={style.dim}>
					At least one {isAutocomplete ? 'option' : 'choice'} should be
					included.
				</p>

				<ReactSortable
					className={style.choices}
					list={state.choices}
					setList={(data) =>
						setState((prev) => ({
							...prev,
							choices: data.map((el: any) => ({
								id: el.id,
								label: el.label,
								description: el.description,
								_id: el._id,
							})),
						}))
					}
					{...sortableOptions}
				>
					{state.choices.map((el, i) => {
						return (
							<Choice
								isAutocomplete={isAutocomplete}
								mode={mode}
								key={el._id}
								index={i}
								choice={el}
								updateChoice={updateChoice}
								removeChoice={removeChoice}
								clearError={clearErrorChoice}
							  error={error.choices.find(choice => choice._id === el._id)}
							/>
						);
					})}
				</ReactSortable>
			</div>
		</Drawer>
	);
}

const options = [
	{ label: 'Vertical', value: 'V' },
	{ label: 'Horizontal', value: 'H' },
];
